import { NextRouter } from 'next/router';

import {
  ContentTypes,
  ThumbnailComponentProps,
  ThumbnailTypes
} from 'src/components/thumbnail/types';
import { ComboOfferState } from 'src/store/interface';
import { mixpanelObject } from 'src/utils/analytics/mixpanel';
import {
  contentPropertySetter,
  contentRentalMixpanelSetter,
  selectDropdownContent
} from 'src/utils/analytics/mixpanel/mixpanelEventsHelper';
import {
  deleteSessionStorage,
  getLocalStorage,
  getSessionStorage,
  setSessionStorage
} from 'src/utils/browserStorageHandler';
import { DynamicObject } from 'src/utils/commonTypes';
import {
  OFFER_VALIDITY_URL,
  REDIRECTION_JSON_URL
} from 'src/utils/constants/ApiConstants';
import { USER_TOKEN } from 'src/utils/constants/localStorageConstants';
import { EVENT } from 'src/utils/constants/utilConstants';
import { convertTimeToHumanReadableFormat } from 'src/utils/convertTimeToHumanReadableFormatUtil';

import { ContentMetaData, ThumbnailOptions } from './types';
import { Season } from '../../services/GraphQL/Queries/types';
import { ASSET_TYPE } from '../constants/AppConstants';
import { PageTypes } from '../constants/stringConstants';
import { IMAGE_PATH_AND_NAME } from '../ImageHelper/ImagePathHelper';
import {
  getPlanMatchingTier,
  checkIsTvod,
  getTVODPrice
} from '../TvodHelper/tvodHelper';
import {
  MIXPANEL_ELEMENT,
  MIXPANEL_SERIES
} from '../analytics/mixpanel/mixpanelConstants';
import { isGlobalCheck } from '../globalCheckHelper';
import { globalUrlHelper } from '../common';
import { httpService } from 'src/services/fetch/HttpService';
import { HTTP_METHODS } from '../constants/HttpMethodsConstants';
export const getEpisodeReleaseDate = (date: string) => {
  const dateStringArr = new Date(date).toDateString().split(' ');
  return `${dateStringArr[2]} ${dateStringArr[1]} ${dateStringArr[3]}`;
};

// Joystick icon check & play5 banner button check
// this flag is off now
export const joystickIconCheckFunc = (item: ContentMetaData) => {
  if (
    item &&
    item.assetType === 101 &&
    'gamify' in item /* && item.gamify.type === "hypercasual" */
  ) {
    return true;
  }
  return false;
};

export abstract class GenerateThumbnailObject {
  static getThumbnailInstance(
    type: ThumbnailTypes,
    tagName: keyof typeof IMAGE_PATH_AND_NAME,
    options: ThumbnailOptions
  ): GenerateThumbnailObject {
    switch (type) {
      case ThumbnailTypes.vertical:
        return new VerticalThumbnail(tagName, options);
      case ThumbnailTypes.upnext:
        return new UpNextThumbnail(tagName, options);
      case ThumbnailTypes.horizontal:
      case ThumbnailTypes.biggerHorizontal:
        return new HorizontalThumbnail(tagName, options);
      case ThumbnailTypes.circular:
        return new CircularThumbnail(tagName, options);
      case ThumbnailTypes.bellybanner:
        return new BellyBannerThumbnail(tagName, options);
      case ThumbnailTypes.plex:
        return new PlexThumbnail(tagName, options);
      default:
        return new VerticalThumbnail(tagName, options);
    }
  }
  abstract generate(): ThumbnailComponentProps;
}

class BellyBannerThumbnail extends GenerateThumbnailObject {
  private type = ThumbnailTypes.bellybanner;
  constructor(
    private tagName: keyof typeof IMAGE_PATH_AND_NAME,
    private thumbnailOptions: ThumbnailOptions
  ) {
    super();
  }

  generate(): ThumbnailComponentProps {
    return {
      id: this.thumbnailOptions.content.id,
      tagName: this.tagName,
      type: ContentTypes.movies,
      thumbnailType: this.type,
      assetSubType: this.thumbnailOptions.content.assetSubType,
      assetType: this.thumbnailOptions.content.assetType,
      // If Display language is other than English then For some title we are getting '0' from BE
      title:
        this.thumbnailOptions.content.title &&
        this.thumbnailOptions.content.title !== '0'
          ? this.thumbnailOptions.content.title
          : this.thumbnailOptions.content.originalTitle,
      imageUrl: this.thumbnailOptions.content.image?.verticalBanner || '',
      slug: this.thumbnailOptions.content.webUrl,
      duration: this.thumbnailOptions.content.duration,
      contentId: this.thumbnailOptions.content.id,
      genres: [],
      tags: [],
      overout: false,
      //TODO: check if this attribute is requied.
      displayTitleOnImg: this.thumbnailOptions.displayTitleOnImage,
      businessType: '',
      billingType: '',
      description: this.thumbnailOptions.content.description,
      content: this.thumbnailOptions.content,
      contentPartner: this.thumbnailOptions.content.contentPartner,
      content_partner_details:
        this.thumbnailOptions.content.content_partner_details,
      joystickIconCheck: joystickIconCheckFunc(this.thumbnailOptions.content)
    };
  }
}

class VerticalThumbnail extends GenerateThumbnailObject {
  private type = ThumbnailTypes.vertical;
  constructor(
    private tagName: keyof typeof IMAGE_PATH_AND_NAME,
    private thumbnailOptions: ThumbnailOptions
  ) {
    super();
  }

  generate(): ThumbnailComponentProps {
    return {
      id: this.thumbnailOptions.content.id,
      tagName: this.tagName,
      type: ContentTypes.movies,
      thumbnailType: this.type,
      assetSubType: this.thumbnailOptions.content.assetSubType,
      assetType: this.thumbnailOptions.content.assetType,
      title:
        // If Display language is other than English then For some title we are getting '0' from BE
        this.thumbnailOptions.content.title &&
        this.thumbnailOptions.content.title !== '0'
          ? this.thumbnailOptions.content.title
          : this.thumbnailOptions.content.originalTitle,
      imageUrl: this.thumbnailOptions.content.image?.verticalBanner || '',
      slug: this.thumbnailOptions.content.webUrl,
      duration: this.thumbnailOptions.content.duration,
      contentId: this.thumbnailOptions.content.id,
      genres: this.thumbnailOptions.content.genres?.length
        ? this.thumbnailOptions.content.genres?.map((genre) => genre.value)
        : [],
      tags:
        this.thumbnailOptions.content.assetType === ASSET_TYPE.Movie &&
        this.thumbnailOptions.content.genres?.length
          ? [
              convertTimeToHumanReadableFormat(
                this.thumbnailOptions.content.duration
              ),
              ...this.thumbnailOptions.content.genres
            ]
              .slice(0, 3)
              ?.map((genre: any) => genre.value || genre)
          : [],
      overout: this.thumbnailOptions.hoverOut,
      //TODO: check if this attribute is requied.
      displayTitleOnImg: this.thumbnailOptions.displayTitleOnImage,
      businessType:
        this.thumbnailOptions.content.businessType ||
        this.thumbnailOptions.content.business_type ||
        '',
      billingType:
        this.thumbnailOptions.content.billingType ||
        this.thumbnailOptions.content.billing_type ||
        '',
      description: this.thumbnailOptions.content.description,
      content: this.thumbnailOptions.content,
      contentPartner: this.thumbnailOptions.content.contentPartner,
      content_partner_details:
        this.thumbnailOptions.content.content_partner_details,
      joystickIconCheck: joystickIconCheckFunc(this.thumbnailOptions.content)
    };
  }
}

class CircularThumbnail extends GenerateThumbnailObject {
  private type = ThumbnailTypes.circular;
  constructor(
    private tagName: keyof typeof IMAGE_PATH_AND_NAME,
    private thumbnailOptions: ThumbnailOptions
  ) {
    super();
  }

  generate(): ThumbnailComponentProps {
    return {
      id: this.thumbnailOptions.content.id,
      tagName: this.tagName,
      type: ContentTypes.movies,
      thumbnailType: this.type,
      assetSubType: this.thumbnailOptions.content.assetSubType,
      assetType: this.thumbnailOptions.content.assetType,
      title:
        // If Display language is other than English then For some title we are getting '0' from BE
        this.thumbnailOptions.content.title &&
        this.thumbnailOptions.content.title !== '0'
          ? this.thumbnailOptions.content.title
          : this.thumbnailOptions.content.originalTitle,
      imageUrl: this.thumbnailOptions.content.image?.verticalBanner || '',
      slug: this.thumbnailOptions.content.webUrl,
      duration: this.thumbnailOptions.content.duration,
      contentId: this.thumbnailOptions.content.id,
      genres: this.thumbnailOptions.content.genres?.length
        ? this.thumbnailOptions.content.genres?.map((genre) => genre.value)
        : [],
      tags:
        this.thumbnailOptions.content.assetType === ASSET_TYPE.Movie &&
        this.thumbnailOptions.content.genres?.length
          ? [
              convertTimeToHumanReadableFormat(
                this.thumbnailOptions.content.duration
              ),
              ...this.thumbnailOptions.content.genres
            ]
              .slice(0, 3)
              ?.map((genre: any) => genre.value || genre)
          : [],
      overout: this.thumbnailOptions.hoverOut,
      //TODO: check if this attribute is requied.
      displayTitleOnImg: this.thumbnailOptions.displayTitleOnImage,
      businessType:
        this.thumbnailOptions.content.businessType ||
        this.thumbnailOptions.content.business_type ||
        '',
      billingType:
        this.thumbnailOptions.content.billingType ||
        this.thumbnailOptions.content.billing_type ||
        '',
      description: this.thumbnailOptions.content.description,
      content: this.thumbnailOptions.content,
      contentPartner: this.thumbnailOptions.content.contentPartner,
      content_partner_details:
        this.thumbnailOptions.content.content_partner_details,
      joystickIconCheck: joystickIconCheckFunc(this.thumbnailOptions.content)
    };
  }
}

class UpNextThumbnail extends GenerateThumbnailObject {
  private type = ThumbnailTypes.upnext;
  constructor(
    private tagName: keyof typeof IMAGE_PATH_AND_NAME,
    private thumbnailOptions: ThumbnailOptions
  ) {
    super();
  }
  generate(): ThumbnailComponentProps {
    return {
      id: this.thumbnailOptions.content.id,
      tagName: this.tagName,
      type: ContentTypes.tvshows,
      thumbnailType: this.type,
      title:
        // If Display language is other than English then For some title we are getting '0' from BE
        this.thumbnailOptions.content.title &&
        this.thumbnailOptions.content.title !== '0'
          ? this.thumbnailOptions.content.title
          : this.thumbnailOptions.content.originalTitle,
      assetSubType: this.thumbnailOptions.content.assetSubType,
      assetType: this.thumbnailOptions.content.assetType,
      imageUrl: this.thumbnailOptions.content.image?.verticalBanner || '',
      slug: this.thumbnailOptions.content.webUrl,
      duration: this.thumbnailOptions.content.duration,
      contentId: this.thumbnailOptions.content.id,
      genres: this.thumbnailOptions.content.genres?.length
        ? this.thumbnailOptions.content.genres?.map((genre) => genre.value)
        : [],
      tags: [],
      overout: false,
      episode: `E${this.thumbnailOptions.content.episodeNumber}`,
      displayTitleOnImg: false,
      businessType:
        this.thumbnailOptions.content.businessType ||
        this.thumbnailOptions.content.business_type ||
        '',
      billingType:
        this.thumbnailOptions.content.billingType ||
        this.thumbnailOptions.content.billing_type ||
        '',
      description: this.thumbnailOptions.content.description,
      date: getEpisodeReleaseDate(this.thumbnailOptions.content.releaseDate),
      content: this.thumbnailOptions.content,
      contentPartner: this.thumbnailOptions.content.contentPartner,
      content_partner_details:
        this.thumbnailOptions.content.content_partner_details,
      joystickIconCheck: joystickIconCheckFunc(this.thumbnailOptions.content)
    };
  }
}

export class HorizontalThumbnail extends GenerateThumbnailObject {
  private type = ThumbnailTypes.horizontal;
  constructor(
    private tagName: keyof typeof IMAGE_PATH_AND_NAME,
    private thumbnailOptions: ThumbnailOptions
  ) {
    super();
  }

  private getTags() {
    switch (this.thumbnailOptions.content.assetSubType) {
      case 'episode': {
        return [
          `E${this.thumbnailOptions.content.episodeNumber}`,
          getEpisodeReleaseDate(this.thumbnailOptions.content.releaseDate)
        ];
      }
      default:
        return [];
    }
  }
  generate(): ThumbnailComponentProps {
    return {
      id: this.thumbnailOptions.content.id,
      tagName: this.tagName,
      type: ContentTypes.news,
      assetSubType: this.thumbnailOptions.content.assetSubType,
      assetType: this.thumbnailOptions.content.assetType,
      thumbnailType: ThumbnailTypes.horizontal,
      title:
        // If Display language is other than English then For some title we are getting '0' from BE
        this.thumbnailOptions.content.title &&
        this.thumbnailOptions.content.title !== '0'
          ? this.thumbnailOptions.content.title
          : this.thumbnailOptions.content.originalTitle,
      imageUrl: this.thumbnailOptions.content.image?.verticalBanner || '',
      slug: this.thumbnailOptions.content.webUrl,
      duration: this.thumbnailOptions.content.duration,
      contentId: this.thumbnailOptions.content.id,
      genres: this.thumbnailOptions.content.genres?.length
        ? this.thumbnailOptions.content.genres?.map((genre) => genre.value)
        : [],
      episode: `E${this.thumbnailOptions.content.episodeNumber as number}`,
      tags: this.getTags(),
      overout: true,
      displayTitleOnImg: false,
      businessType:
        this.thumbnailOptions.content.businessType ||
        this.thumbnailOptions.content.business_type ||
        '',
      billingType:
        this.thumbnailOptions.content.billingType ||
        this.thumbnailOptions.content.billing_type ||
        '',
      description: this.thumbnailOptions.content.description,
      date: getEpisodeReleaseDate(this.thumbnailOptions.content.releaseDate),
      content: this.thumbnailOptions.content,
      contentPartner: this.thumbnailOptions.content.contentPartner,
      content_partner_details:
        this.thumbnailOptions.content.content_partner_details,
      joystickIconCheck: joystickIconCheckFunc(this.thumbnailOptions.content)
    };
  }
}

class PlexThumbnail extends GenerateThumbnailObject {
  constructor(
    private tagName: keyof typeof IMAGE_PATH_AND_NAME,
    private thumbnailOptions: ThumbnailOptions
  ) {
    super();
  }

  generate(): ThumbnailComponentProps {
    return {
      id: this.thumbnailOptions.content.id,
      tagName: this.tagName,
      type: ContentTypes.news,
      assetSubType: this.thumbnailOptions.content.assetSubType,
      assetType: this.thumbnailOptions.content.assetType,
      thumbnailType: ThumbnailTypes.plex,
      title:
        // If Display language is other than English then For some title we are getting '0' from BE
        this.thumbnailOptions.content.title &&
        this.thumbnailOptions.content.title !== '0'
          ? this.thumbnailOptions.content.title
          : this.thumbnailOptions.content.originalTitle,
      imageUrl: this.thumbnailOptions.content.image?.verticalBanner || '',
      slug: this.thumbnailOptions.content.webUrl,
      duration: this.thumbnailOptions.content.duration,
      contentId: this.thumbnailOptions.content.id,
      ageRating: this.thumbnailOptions.content.ageRating,
      genres: this.thumbnailOptions.content.genres?.length
        ? this.thumbnailOptions.content.genres?.map((genre) => genre.value)
        : [],
      episode: `E${this.thumbnailOptions.content.episodeNumber as number}`,
      overout: true,
      displayTitleOnImg: false,
      businessType:
        this.thumbnailOptions.content.businessType ||
        this.thumbnailOptions.content.business_type ||
        '',
      billingType:
        this.thumbnailOptions.content.billingType ||
        this.thumbnailOptions.content.billing_type ||
        '',
      description: this.thumbnailOptions.content.description,
      date: getEpisodeReleaseDate(this.thumbnailOptions.content.releaseDate),
      content: this.thumbnailOptions.content,
      contentPartner: this.thumbnailOptions.content.contentPartner,
      content_partner_details:
        this.thumbnailOptions.content.content_partner_details,
      joystickIconCheck: joystickIconCheckFunc(this.thumbnailOptions.content)
    };
  }
}

export const getFirstItemFromArray = (arr: any[], defaultReturn?: any) => {
  const copyArr = [...arr];
  return copyArr && Array.isArray(copyArr) && copyArr.length > 0
    ? copyArr[0]
    : defaultReturn;
};

export const objectHasProperty = (obj: DynamicObject, attributeKey: string) => {
  return (
    typeof obj === 'object' &&
    Object.prototype.hasOwnProperty.call(obj, attributeKey)
  );
};

export const filterComboOffers = (offers: any[], eventsOnly = false) => {
  let requiredOffers = [];

  // Take only "isMandatory: 0" offers
  if (offers && offers.length) {
    requiredOffers = offers.filter(
      (offer) => offer.isMandatory && parseInt(offer.isMandatory, 10) === 0
    );
  }

  // if 'eventsOnly' flag is true, then take only "category: event" offers from required offers
  if (eventsOnly) {
    requiredOffers = requiredOffers.filter(
      (offer) => offer.category && offer.category.toLowerCase() === EVENT
    );
  }

  return requiredOffers;
};

const checkActiveYearylOrHalfYearlyPremiumPlan = (
  currentActiveSubscriptions: any
) => {
  for (let i = 0; i < currentActiveSubscriptions.length; i += 1) {
    const {
      subscription_plan: {
        billing_frequency: billingFrequency = '',
        billing_type: billingType = '',
        tv_show_audio_languages: tvShowAudioLanguages = [],
        movie_audio_languages: movieAudioLanguages = [],
        channel_audio_languages: channelAudioLanguages = []
      } = {}
    } = currentActiveSubscriptions[i];
    if (
      billingType === 'premium' &&
      (billingFrequency === 365 || billingFrequency === 180) &&
      (tvShowAudioLanguages.length === 0 ||
        movieAudioLanguages.length === 0 ||
        channelAudioLanguages.length === 0)
    ) {
      return true;
    }
  }
  return false;
};

export const isOfferEnabled = async ({
  tier,
  tvodRentals,
  tierMatchingPlans = [],
  currentActiveSubscriptions = []
}: any) => {
  const isLoggedIn = !!getLocalStorage(USER_TOKEN);

  // if tvodPlan is not passed, then get respective tvodPlan based on tier value
  if (!tierMatchingPlans.length && tier && tvodRentals) {
    tierMatchingPlans = getPlanMatchingTier(tier, tvodRentals.tvodPlans);
  }

  let isOfferEnabled = false;
  if (tierMatchingPlans.length) {
    const matchedPlan = getFirstItemFromArray(tierMatchingPlans);
    const offers =
      matchedPlan &&
      objectHasProperty(matchedPlan, 'offers') &&
      filterComboOffers(matchedPlan.offers);
    const offerExist = offers && offers.length > 0;
    if (isLoggedIn && offerExist) {
      const comboValidityData = await validateOffer({ offerId: offers[0].id });
      const isOfferAvailed = isComboAvailed(comboValidityData);
      isOfferEnabled = !isOfferAvailed;

      // this is just hack - Offer validity API response is wrong if user is premium user
      // User should not be eligible for combo if he has subscribed any premium half yearly and yearly plan
      if (isOfferEnabled) {
        if (currentActiveSubscriptions?.length) {
          const isActiveYearylOrHalfYearlyPremiumPlan =
            checkActiveYearylOrHalfYearlyPremiumPlan(
              currentActiveSubscriptions
            );
          isOfferEnabled = isActiveYearylOrHalfYearlyPremiumPlan
            ? false
            : isOfferEnabled;
        }
      }
    } else {
      isOfferEnabled = offerExist;
    }
  }
  return isOfferEnabled;
};
export const getOfferValidityUrl = (id: string) => {
  return `${OFFER_VALIDITY_URL}/${id}`;
};

export const validateOffer = async ({ offerId, clear = false }: any) => {
  const url = getOfferValidityUrl(offerId);
  const sessionStorageKey = url;
  if (clear) {
    deleteSessionStorage(sessionStorageKey);
  }

  const currentValue = getSessionStorage(sessionStorageKey);
  if (
    currentValue &&
    typeof currentValue === 'object' &&
    Object.keys(currentValue).length
  ) {
    return currentValue;
  }

  // TODO: Write a function to handle gwapigetWithUserToken
  //   const data = await gwapigetWithUserToken({ url });
  const data = {};

  let newValue;
  if (data && typeof data === 'object' && Object.keys(data).length) {
    newValue = { ...data };
  }
  if (newValue) {
    // only set in session storage if not undefined
    setSessionStorage(sessionStorageKey, newValue);
  }
  return newValue;
};

const isComboAvailed = (comboValidityData: any) => {
  let isOfferAvailed = false;
  if (comboValidityData && objectHasProperty(comboValidityData, 'code')) {
    isOfferAvailed = comboValidityData.code !== 0;
  }
  return isOfferAvailed;
};

const handleComboOfferEnabled = async (
  { tier, currentActiveSubscriptions, tvodRentals }: any,
  setComboOfferState: (data: ComboOfferState) => void
) => {
  // setComboOfferEnabled(true);
  setComboOfferState({ isComboOfferEnabledPending: true });
  const isComboOfferEnabled = await isOfferEnabled({
    tier,
    tvodRentals,
    currentActiveSubscriptions
  });
  setComboOfferState({
    isComboOfferEnabledPending: false,
    isComboOfferEnabled
  });

  // setComboOfferEnabled(false);
};

const getPlanDetails = (tier: any, tvodRentals: any) => {
  return getPlanMatchingTier(tier, tvodRentals);
};

export const getExpiryTime = (
  expiry: string,
  z5Strings: DynamicObject,
  licenseExpiry = false
) => {
  const currentDate: any = new Date();
  const expiryDate: any = new Date(expiry);
  if (licenseExpiry && expiry) {
    const noOfHours =
      expiryDate - currentDate + currentDate.getTimezoneOffset() * 60 * 1000;
    const hours_left = noOfHours / (1000 * 60 * 60);
    return hours_left;
  }
  const noOfDays =
    Date.UTC(
      expiryDate.getFullYear(),
      expiryDate.getMonth(),
      expiryDate.getDate(),
      expiryDate.getHours(),
      expiryDate.getMinutes()
    ) -
    Date.UTC(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
      currentDate.getHours(),
      currentDate.getMinutes()
    );
  let seconds = Math.floor(noOfDays / 1000);
  let minute = Math.floor(seconds / 60);
  seconds %= 60;
  let hour = Math.floor(minute / 60);
  minute %= 60;
  const day = Math.floor(hour / 24);
  hour %= 24;
  let no_of_days = 0;
  let no_of_min = 0;
  let no_of_hours = 0;
  let period = z5Strings && z5Strings.minutes_minute;
  let expiryPeriod = { period, number: no_of_min };
  if (day > 0) {
    if (minute >= 30) {
      hour += 1;
    }
    if (hour > 12) {
      no_of_days = 1;
    } else if (hour && hour <= 12) {
      no_of_days = 0.5;
    }
    no_of_days += day;
    if (no_of_days === 1) {
      period = z5Strings && z5Strings.day_day;
    } else {
      period = z5Strings && z5Strings.days_days;
    }
    expiryPeriod = { period, number: no_of_days };
  } else if (hour >= 1) {
    if (minute > 30) {
      hour += 1;
    } else if (minute > 0) {
      hour += 0.5;
    }
    no_of_hours = hour;
    if (no_of_hours === 1) {
      period = z5Strings && z5Strings.hour_hour;
    } else {
      period = z5Strings && z5Strings.hours_hours;
    }
    expiryPeriod = { period, number: no_of_hours };
  } else if (minute > 0) {
    no_of_min += minute;
    if (no_of_min === 1) {
      period = z5Strings && z5Strings.minutes_minute;
    } else {
      period = z5Strings && z5Strings.minutes_minutes;
    }
    expiryPeriod = { period, number: no_of_min };
  }
  return expiryPeriod;
};

// Check tvod event
export const isTvodEvent = ({ businessType, tier, tvodRentals }: any) => {
  let isEvent = false;
  if (tier && tvodRentals) {
    const tierMatchingPlans = getPlanMatchingTier(tier, tvodRentals);

    if (tierMatchingPlans.length) {
      const matchedPlan = getFirstItemFromArray(tierMatchingPlans);
      const { category = '' } = matchedPlan || {};
      if (category && typeof category === 'string') {
        isEvent = category.toLowerCase() === 'event';
      }
    }
  }
  return checkIsTvod(businessType) && isEvent;
};

export const checkIfTvod = (
  data: any,
  updateTvodState: any,
  updatePlayerState: any,
  setComboOfferState: (data: ComboOfferState) => void
) => {
  const {
    singlePlayMovieData,
    tvodRentals,
    z5StringsEn,
    countryList,
    isInitialDataPending,
    movieId,
    currentActiveSubscriptions
  } = data;
  let activeRentalData,
    minutesToExpiry,
    isRentalPlaybackExpired = [];
  if (
    singlePlayMovieData &&
    Object.keys(singlePlayMovieData).length &&
    singlePlayMovieData.assetDetails &&
    Object.keys(singlePlayMovieData.assetDetails).length &&
    checkIsTvod(singlePlayMovieData.assetDetails.business_type) &&
    !isInitialDataPending
  ) {
    const { tier } = singlePlayMovieData.assetDetails;
    const tvodPrice: any = tier ? getTVODPrice(tvodRentals, tier) : {};

    const planDetails = getPlanDetails(tier, tvodRentals);
    planDetails[0] = { ...planDetails[0], price: tvodPrice.price };

    // Check tvod has event or not
    const tvodEvent = isTvodEvent({
      businessType: singlePlayMovieData.assetDetails.business_type,
      tier,
      tvodRentals
    });
    updateTvodState({
      isTvodContent: true,
      tvodPriceObj: tvodPrice,
      planDetails,
      isTvodEvent: tvodEvent
    });
    handleComboOfferEnabled(
      { tier, currentActiveSubscriptions, tvodRentals },
      setComboOfferState
    );
    if (
      tvodRentals &&
      tvodRentals.activeRentalPurchases &&
      tvodRentals.activeRentalPurchases.length > 0
    ) {
      activeRentalData = tvodRentals.activeRentalPurchases.filter(
        (item: any) => item.asset_id === movieId
      );

      // to fetch minuites to expire mixpanel
      const expiryTimeObj: any = activeRentalData.length
        ? getExpiryTime(
            activeRentalData[0].playback_end_date ||
              activeRentalData[0].purchase_end,
            z5StringsEn.TVOD_CONSUMPTION
          )
        : {};
      const duration = Object.keys(expiryTimeObj).length
        ? `${expiryTimeObj.number} ${expiryTimeObj.period}`
        : 'N/A';
      minutesToExpiry = duration;

      contentPropertySetter(
        mixpanelObject,
        singlePlayMovieData.assetDetails,
        {}
      );

      contentRentalMixpanelSetter(
        mixpanelObject,
        singlePlayMovieData.assetDetails,
        activeRentalData,
        minutesToExpiry
      );
    }

    if (
      tvodRentals &&
      tvodRentals.expiredRentalPurchases &&
      tvodRentals.expiredRentalPurchases.length > 0
    ) {
      isRentalPlaybackExpired = tvodRentals.expiredRentalPurchases.filter(
        (item: any) =>
          item.asset_id === movieId &&
          item.playback_state &&
          (item.playback_state === 'playback_ended' ||
            item.playback_state === 'plan_expired')
      );
    }

    if (
      singlePlayMovieData.assetDetails.asset_subtype === 'movie' &&
      activeRentalData &&
      activeRentalData.length > 0
    ) {
      if (!isRentalPlaybackExpired.length) {
        updatePlayerState({ togglePlayer: false, triggerPlayer: false });
        updateTvodState({ isUserEntitledForTvod: true });
      }
    }
    updateTvodState({ isTVODLogicResolved: true });
  }
};

// Generating all Episode intervals
export const getAllEpisodeIntervals = ({
  totalEpisodes,
  baseEpisodeNumber,
  episodesString
}: {
  totalEpisodes: number;
  baseEpisodeNumber: number;
  episodesString: string;
}) => {
  const intervals = [];
  // if off-air show
  if (baseEpisodeNumber === 1) {
    for (let i = 1; i <= totalEpisodes; i += 10) {
      let l = i + 9;
      if (l > totalEpisodes) {
        l = totalEpisodes;
      }
      intervals.push(`${episodesString} ${i} - ${l}`);
    }
    // if on-air show
  } else {
    for (let i = totalEpisodes; i >= 1; i -= 10) {
      let l = i - 9;
      if (l <= 0) {
        l = 1;
      }
      intervals.push(`${episodesString} ${l} - ${i}`);
    }
  }
  return intervals;
};

export const checkKidsWithGenre = (genres: any[]): boolean => {
  let isKidType = false;
  let isEduaraCard = false;

  if (genres) {
    genres.forEach((ele) => {
      const { id = '' } = ele || {};

      if (id === 'Kids' || id === 'Kid') {
        isKidType = true;
      } else if (id === 'Education') {
        isEduaraCard = true;
      }
    });
  }

  return isKidType && !isEduaraCard;
};

// Season Page URL creation
export const showMoreClickHandler = (
  singlePlayEpisodeData: any,
  urlParms: string,
  router: NextRouter,
  getUrl: boolean,
  seasons: Season[]
): string | null => {
  const urlParams = urlParms.split('/');
  const assetDetails = singlePlayEpisodeData?.assetDetails;
  const seasonsArray = seasons || [];
  const seasonNum =
    seasonsArray && seasonsArray[seasonsArray.length - 1]?.orderId;
  const assetTitle = assetDetails?.isOriginals ? 'web-series' : 'tv-shows';
  const { showName, showId } = router.query;
  const isKids =
    assetDetails &&
    assetDetails.genres &&
    checkKidsWithGenre(assetDetails.genres);
  let url = `/${assetTitle}/details/${showName}/${showId}`;
  if (isGlobalCheck(router?.asPath)) {
    url = `/global/${assetTitle}/details/${showName}/${showId}`;
  }
  if (isKids) {
    url = `/kids/kids-shows/${urlParams[3]}/${urlParams[4]}`;
  }

  if (seasonNum === 1) {
    if (getUrl) {
      return (url += `/episodes`);
    }
    url += `/episodes`;
  } else {
    if (getUrl) {
      return (url += `/season-${seasonNum}/episodes`);
    }
    url += `/season-${seasonNum}/episodes`;
  }
  router.push(url);
  return null;
};

// Identify tv show or web series
export const determineShowOrWebSeries = (showDetailsObj: any): string => {
  if (showDetailsObj && showDetailsObj?.asset_subtype === 'original') {
    return 'Web-series';
  } else if (showDetailsObj) {
    return 'Show';
  }

  return '';
};

export const getConsumptionPageType = (contentType: string) => {
  if (
    contentType === ContentTypes.tvshows ||
    contentType === ContentTypes.webSeries
  ) {
    return PageTypes.EPISODE_CONSUMPTION;
  }
  return PageTypes.MOVIE_CONSUMPTION;
};

// Trigger mixpanel event On selecting content from dropdown list.
export const triggerMixpanelSelectDropdown = (
  selectedOption: string,
  tvShowTitle: string | undefined
) => {
  let elementName = 'N/A';
  if (selectedOption) {
    elementName = selectedOption;
  }
  mixpanelObject[MIXPANEL_ELEMENT] = elementName;
  mixpanelObject[MIXPANEL_SERIES] = tvShowTitle;
  selectDropdownContent();
};

export const contentSpecificCheck = async (
  episodeId: string,
  pathName: string
) => {
  //Redirecting the Old contentId URL to New content Id URL (only in Global regions if specified in json)
  //check json for more info on the structure
  try {
    const url = REDIRECTION_JSON_URL;
    const contentRedirectionIdsJson = await httpService.fetchApiData({
      url,
      type: HTTP_METHODS.GET
    });

    const content = contentRedirectionIdsJson[episodeId];
    if (content && content.enabled) {
      const shouldRedirect = content.onlyGlobal
        ? isGlobalCheck(pathName)
        : true;
      if (shouldRedirect) {
        const newEpisodeId = content.redirectTo ?? episodeId; // Default to episodeId if redirectTo is null or undefined
        const updatedURL = globalUrlHelper(
          pathName.replace(episodeId, newEpisodeId)
        );

        return {
          redirect: {
            destination: encodeURI(updatedURL),
            permanent: true
          }
        };
      }
    }
  } catch (e) {
    console.error('Error while fetching contentRedirection json: ', e);
    return null;
  }
};
